'use client';

import 'BomdisoftComponents/page-layout/styles.css';
import type { PageLayoutTabs } from 'BomdisoftComponents/page-layout/index';
import { UnderlineTabs } from 'BomdisoftComponents/tab/index';
import { usePersisted } from '@bomdisoft/store/persisted/next';
import type { PersistedHandle } from '@bomdisoft/store/persisted/types';
import type { ReactNode } from 'react';

type Props<Value extends string> = { tabs: PageLayoutTabs<Value>; testId: string };

/**
 * The tab row and the panel under it.
 *
 * Split out of `PageLayout` because it is the only part that calls a hook: the
 * layout renders it conditionally, and a hook cannot live in a branch. The
 * handle-backed case is split again for the same reason, so `usePersisted` is
 * never called on a render path that has no handle.
 */
export const TabbedBody = <Value extends string>({ tabs, testId }: Props<Value>): ReactNode => (tabs.handle === undefined ? <Row pick={tabs.onChange} picked={tabs.value as Value} tabs={tabs} testId={testId} /> : <HandleRow handle={tabs.handle} tabs={tabs} testId={testId} />);

const HandleRow = <Value extends string>({ handle, tabs, testId }: Props<Value> & { handle: PersistedHandle<Value> }): ReactNode => {
  const [value, write] = usePersisted(handle);
  return (
    <Row
      pick={next => {
        write(next);
        tabs.onChange?.(next);
      }}
      picked={value}
      tabs={tabs}
      testId={testId}
    />
  );
};

const Row = <Value extends string>({ pick, picked, tabs, testId }: Props<Value> & { pick: (value: Value) => void; picked: Value }): ReactNode => {
  const active = tabs.items.find(item => item.value === picked) ?? tabs.items[0];

  return (
    <>
      <UnderlineTabs ariaControls={`${testId}-body`} label={tabs.label} onChange={pick} tabs={tabs.items.map(item => ({ label: item.label, value: item.value }))} testIdPrefix={`${testId}-tab`} value={picked} />
      {tabs.common}
      <div className='bomdisoft-page-layout-body' data-testid={`${testId}-body`} id={`${testId}-body`} role='tabpanel'>
        {active?.panel}
      </div>
    </>
  );
};
